/**
 * Stepped Rx / Ophthalmic configurator modal.
 * Depends on window.pdp + section helpers: open/close patched after load.
 */
(function () {
  const STEP_META = {
    'oph-type': { title: 'Choose Your Type', subtitle: 'Pick how you want to wear these frames' },
    'lens-type': { title: 'Select Your Lens Type', subtitle: 'Choose your vision type' },
    'lens-color': { title: 'Choose Your Lens Color', subtitle: 'Pick the look that fits your style' },
    'lens-upgrade': { title: 'Upgrade Your Lenses', subtitle: 'Choose a lens material to continue' },
    'rx-entry': { title: 'Enter Your Prescription', subtitle: 'Upload, enter manually, or send it later' }
  }

  const state = {
    steps: [],
    index: 0,
    sendLater: false,
    sameAsBefore: false,
    pdMode: '1',
    rxMethod: '',
    uploadUrl: '',
    uploadName: '',
    ocrPendingConfirm: false,
    ocrFilled: false,
    forceShowManual: false,
    cartRxProps: null
  }

  function modal() {
    return document.getElementById('rx-config-modal')
  }

  function money(amount) {
    if (typeof window.formatSepDesignOfferMoney === 'function') {
      return window.formatSepDesignOfferMoney(amount)
    }
    const n = parseFloat(amount)
    if (isNaN(n)) return '—'
    return '$' + (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, '')
  }

  /** Same sale math as PDP `.price-block.price` (30% off for sun/rx/oph). */
  function resolveDisplayedVariantPrices() {
    const variant = window.pdp?.selectedVariant
    const productType = window.pdp?.currentProduct?.product_type
    let amount = variant?.price != null ? parseFloat(variant.price) : null
    let compareAmount = null

    if (amount != null && !isNaN(amount)) {
      if (
        productType === 'Sunglasses' ||
        productType === 'Prescription' ||
        productType === 'Ophthalmic' ||
        (typeof window.isOphthalmicPdpProduct === 'function' &&
          window.isOphthalmicPdpProduct(window.pdp.currentProduct))
      ) {
        compareAmount = amount
        amount = amount * 0.7
      } else {
        const variantCompare = variant?.compare_at_price != null ? parseFloat(variant.compare_at_price) : null
        if (variantCompare != null && !isNaN(variantCompare) && variantCompare > amount) {
          compareAmount = variantCompare
        }
      }
    } else {
      amount = null
    }

    return { amount, compareAmount }
  }

  function currentStepId() {
    return state.steps[state.index] || null
  }

  function isOphBlueLightOrPlain() {
    const t = window.pdp?.rx?.['oph-type']
    return t === 'blue-light' || t === 'plain'
  }

  function isOphReaders() {
    return window.pdp?.rx?.['oph-type'] === 'readers'
  }

  function isOphPrescription() {
    return window.pdp?.rx?.['oph-type'] === 'prescription'
  }

  function buildSteps() {
    const type = window.pdp?.currentProduct?.product_type
    const oph = window.pdp?.rx?.['oph-type']
    let steps
    if (type === 'Prescription') {
      steps = ['lens-type', 'lens-color', 'lens-upgrade']
    } else if (type === 'Ophthalmic') {
      if (!oph) steps = ['oph-type']
      else if (oph === 'blue-light' || oph === 'plain') steps = ['oph-type']
      else if (oph === 'readers') steps = ['oph-type', 'lens-type', 'lens-upgrade']
      else steps = ['oph-type', 'lens-type', 'lens-upgrade']
    } else {
      steps = ['lens-type']
    }
    // Match rx-form isEligibleForRx: only true Prescription lines need RX entry.
    // Readers / plain / blue-light do not.
    const needsRxEntry =
      type === 'Prescription' ||
      (type === 'Ophthalmic' && oph === 'prescription')
    if (needsRxEntry && steps.includes('lens-upgrade') && !steps.includes('rx-entry')) {
      steps = steps.concat(['rx-entry'])
    }
    return steps
  }

  function stepPanelSelector(step) {
    if (step === 'oph-type') return '#oph-type-panel'
    if (step === 'lens-type') return '#lens-type-panel'
    if (step === 'lens-color') return '#lens-color-panel'
    if (step === 'lens-upgrade') return '#lens-upgrade-panel'
    return null
  }

  const BASE_LENS_UPGRADES = ['standard', 'impact-resistant', 'high-index']

  function hasBaseLensUpgradeSelected() {
    const upgrades = window.pdp?.rx?.['lens-upgrade']
    if (!upgrades) return false
    return BASE_LENS_UPGRADES.some((k) => !!upgrades[k])
  }

  function hasVisibleBaseLensUpgradeUi() {
    return !!document.querySelector(
      '#lens-upgrade-panel .dropdown-option.selected[data-value="standard"],' +
        '#lens-upgrade-panel .dropdown-option.selected[data-value="impact-resistant"],' +
        '#lens-upgrade-panel .dropdown-option.selected[data-value="high-index"]'
    )
  }

  /** Coatings alone are not a valid Shopify variant — always need Standard / IR / HI. */
  function ensureBaseLensUpgrade() {
    if (!window.pdp?.rx?.['lens-upgrade']) return false
    if (hasBaseLensUpgradeSelected() && hasVisibleBaseLensUpgradeUi()) return true

    const pickOrder = ['standard', 'impact-resistant', 'high-index']
    for (let i = 0; i < pickOrder.length; i++) {
      const handle = pickOrder[i]
      const opt = document.querySelector(
        '#lens-upgrade-panel .dropdown-option[data-value="' + handle + '"]'
      )
      if (!opt || opt.classList.contains('hidden') || opt.getAttribute('aria-disabled') === 'true') {
        continue
      }
      if (!opt.classList.contains('selected')) opt.click()
      return hasBaseLensUpgradeSelected()
    }
    return false
  }

  function stepHasRequiredSelection() {
    const step = currentStepId()
    if (step === 'rx-entry') return !!state.rxMethod
    const panelSel = stepPanelSelector(step)
    if (!panelSel) return true
    // Upgrade step: coatings alone unlock Continue but do not map to a variant.
    // Require a visible base lens material (Standard / Impact Resistant / High Index).
    if (step === 'lens-upgrade') {
      return hasVisibleBaseLensUpgradeUi()
    }
    // Only a visible UI selection unlocks Continue / Add to cart
    return !!document.querySelector(panelSel + ' .dropdown-option.selected')
  }

  function clearModalRequiredSelections(options) {
    const m = modal()
    if (!m || !window.pdp?.rx) return
    const preserveOphType = !!(options && options.preserveOphType)

    // Keep lens color so reopen can continue where shopper left off
    const preservedLensColor = String(window.pdp.rx['lens-color'] || '').trim()
    const preservedOphType = preserveOphType ? String(window.pdp.rx['oph-type'] || '').trim() : ''

    ;['#oph-type-panel', '#lens-type-panel', '#lens-color-panel', '#lens-upgrade-panel'].forEach((panelSel) => {
      if (preserveOphType && panelSel === '#oph-type-panel') return
      m.querySelectorAll(panelSel + ' .dropdown-option.selected').forEach((el) => {
        el.classList.remove('selected')
      })
    })
    if (!preserveOphType) {
      window.pdp.rx['oph-type'] = ''
    } else if (preservedOphType) {
      window.pdp.rx['oph-type'] = preservedOphType
      const opt = m.querySelector(
        '#oph-type-panel .dropdown-option[data-value="' + preservedOphType + '"]'
      )
      if (opt) opt.classList.add('selected')
    }
    window.pdp.rx['lens-type'] = ''
    window.pdp.rx['lens-color'] = ''
    if (window.pdp.rx['lens-upgrade']) {
      Object.keys(window.pdp.rx['lens-upgrade']).forEach((k) => {
        window.pdp.rx['lens-upgrade'][k] = false
      })
      const upgradeTrigger = m.querySelector('[data-dropdown="lens-upgrade"]')
      if (upgradeTrigger) {
        upgradeTrigger.dispatchEvent(new CustomEvent('lens-upgrade:availability-changed'))
      }
    }

    restorePreservedLensColor(preservedLensColor)
    if (typeof window.updateLensStyleInfoMessage === 'function') {
      window.updateLensStyleInfoMessage()
    }
  }

  function restorePreservedLensColor(lensColor) {
    if (!lensColor || !window.pdp?.rx) return
    const m = modal()
    if (!m) return
    const opt = Array.from(m.querySelectorAll('#lens-color-panel .dropdown-option[data-value]')).find(
      (el) => el.getAttribute('data-value') === lensColor
    )
    if (!opt || opt.classList.contains('hidden') || opt.getAttribute('aria-disabled') === 'true') {
      return
    }
    window.pdp.rx['lens-color'] = lensColor
    opt.classList.add('selected')
    if (typeof window.updateLensStyleInfoMessage === 'function') {
      window.updateLensStyleInfoMessage()
    }
  }

  function toOptionHandle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/_/g, '-')
      .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
  }

  function clickOptionIfNeeded(selector) {
    const el = document.querySelector(selector)
    if (!el || el.classList.contains('hidden') || el.getAttribute('aria-disabled') === 'true') return false
    if (!el.classList.contains('selected')) el.click()
    return el.classList.contains('selected')
  }

  /** Prefill modal steps from the visited variant (edit URL / variant param). */
  function applySelectionsFromVisitedVariant() {
    const variant = window.pdp?.selectedVariant
    const productType = window.pdp?.currentProduct?.product_type
    if (!variant || (productType !== 'Prescription' && productType !== 'Ophthalmic')) return

    const url = new URLSearchParams(window.location.search)
    const urlType = url.get('type')
    const urlUpgrades = url.get('upgrades')

    const applyUpgrades = (option3) => {
      let upgrades = String(option3 || urlUpgrades || '')
        .split(/\s*\+\s*/)
        .map(toOptionHandle)
        .filter(Boolean)
      if (!upgrades.length) upgrades = ['standard']
      if (!upgrades.includes('impact-resistant') && !upgrades.includes('high-index') && !upgrades.includes('standard')) {
        upgrades.push('standard')
      }
      upgrades.forEach((upgrade) => {
        clickOptionIfNeeded(`#lens-upgrade-panel .dropdown-option[data-value="${upgrade}"]`)
      })
    }

    if (productType === 'Prescription') {
      const lensType = toOptionHandle(variant.option1 || urlType)
      const lensColor = toOptionHandle(variant.option2)
      clickOptionIfNeeded(`#lens-type-panel .dropdown-option[data-value="${lensType}"]`)
      clickOptionIfNeeded(`#lens-color-panel .dropdown-option[data-value="${lensColor}"]`)
      applyUpgrades(variant.option3)
      return
    }

    // Ophthalmic: option1 = type (prescription/readers/...), option2 = lens type
    if (window.pdp.ophSplitArchitecture && window.pdp.ophSplitBlProduct?.variants?.some((v) => v.id === variant.id)) {
      clickOptionIfNeeded('#oph-type-panel .dropdown-option[data-value="blue-light"]')
      return
    }
    if (window.pdp.ophSplitArchitecture && window.pdp.ophSplitPlProduct?.variants?.some((v) => v.id === variant.id)) {
      clickOptionIfNeeded('#oph-type-panel .dropdown-option[data-value="plain"]')
      return
    }

    const ophType = toOptionHandle(variant.option1)
    const lensType = toOptionHandle(variant.option2 || urlType)
    clickOptionIfNeeded(`#oph-type-panel .dropdown-option[data-value="${ophType}"]`)
    if (ophType === 'blue-light' || ophType === 'plain') return
    clickOptionIfNeeded(`#lens-type-panel .dropdown-option[data-value="${lensType}"]`)
    applyUpgrades(variant.option3)
  }

  function syncContinueEnabledState() {
    const continueBtn = document.getElementById('rx-config-continue')
    if (!continueBtn || continueBtn.dataset.loading === '1') return
    const ready = stepHasRequiredSelection()
    continueBtn.disabled = !ready
    continueBtn.setAttribute('aria-disabled', ready ? 'false' : 'true')
    continueBtn.classList.toggle('is-step-locked', !ready)
  }

  function syncStepVisibility() {
    const m = modal()
    if (!m) return
    const step = currentStepId()
    m.setAttribute('data-step', step || '')

    if (step !== 'oph-type' && typeof window.hideOphTypeOosForm === 'function') {
      window.hideOphTypeOosForm()
    }

    m.querySelectorAll('.dropdown-wrapper').forEach((wrap) => {
      wrap.classList.remove('is-step-visible')
      const drop = wrap.querySelector('.dropdown')
      if (!drop) return
      if (step === 'oph-type' && drop.classList.contains('oph-type')) wrap.classList.add('is-step-visible')
      if (step === 'lens-type' && drop.classList.contains('lens-type')) wrap.classList.add('is-step-visible')
      if (step === 'lens-color' && drop.classList.contains('lens-color')) {
        wrap.classList.add('is-step-visible')
      }
      if (step === 'lens-upgrade' && drop.classList.contains('lens-upgrade')) wrap.classList.add('is-step-visible')
    })

    if (step === 'lens-upgrade') {
      ensureBaseLensUpgrade()
    }

    const rxStep = document.getElementById('rx-config-rx-step')
    if (rxStep) rxStep.hidden = step !== 'rx-entry'
    if (step === 'rx-entry') {
      syncAddPowerVisibility()
      prepareSameAsBefore()
    }

    const summaryBoard = document.getElementById('rx-config-summary-board')
    if (summaryBoard) summaryBoard.hidden = true

    const continueBtn = document.getElementById('rx-config-continue')
    const atc = m.querySelector('.rx-config-modal__atc')
    const last = state.index >= state.steps.length - 1
    const earlyAtc = step === 'oph-type' && isOphBlueLightOrPlain()
    const showAtc = last || earlyAtc

    // Keep summary visible through upgrade + Rx entry / ATC
    const footerSummary = document.getElementById('rx-config-summary')
    if (footerSummary) footerSummary.hidden = false

    let meta = STEP_META[step] || { title: 'Customize your pair', subtitle: '' }
    if (step === 'lens-type' && window.pdp?.currentProduct?.product_type === 'Safety') {
      meta = { title: 'Select Your Lens Type', subtitle: 'Choose the lens for your safety pair' }
    }
    const titleEl = document.getElementById('rx-config-modal-title')
    const subEl = document.getElementById('rx-config-modal-subtitle')
    if (titleEl) titleEl.textContent = meta.title
    if (subEl) {
      subEl.textContent = meta.subtitle || ''
      subEl.hidden = !meta.subtitle
    }
    if (typeof window.updateLensStyleInfoMessage === 'function') {
      window.updateLensStyleInfoMessage()
    }

    const back = document.getElementById('rx-config-back')
    if (back) back.hidden = state.index <= 0

    if (continueBtn && atc) {
      if (showAtc) {
        continueBtn.hidden = false
        continueBtn.dataset.final = '1'
        if (continueBtn.dataset.loading !== '1') {
          continueBtn.textContent = 'Add to cart'
        }
        atc.hidden = true
      } else {
        continueBtn.hidden = false
        continueBtn.textContent = 'Continue'
        continueBtn.dataset.final = '0'
        continueBtn.dataset.loading = '0'
        atc.hidden = true
      }
    }

    syncContinueEnabledState()
    updatePreviewMeta()
    syncRxLensColorProductImages()
    syncRxConfigTryOnButton()
    updateSummary()
    if (typeof window.keepRxConfigOptionPanelsOpen === 'function') {
      window.keepRxConfigOptionPanelsOpen()
    }
  }

  function resolvePreviewImageSrc() {
    const product = window.pdp?.currentProduct
    const productHandle = product?.handle
    const variant = window.pdp?.selectedVariant
    const lensColor = window.pdp?.rx?.['lens-color']

    // Prefer real catalog media for the selected lens color (same path as color tiles)
    if (lensColor) {
      const colorSrc = resolveLensColorProductImageSrc(lensColor)
      if (colorSrc) return colorSrc
    }

    const safetySrc = resolveSafetyLensPreviewImageSrc()
    if (safetySrc) return safetySrc

    const featured =
      variant?.featured_image?.src ||
      variant?.featured_media?.preview_image?.src ||
      (typeof variant?.featured_image === 'string' ? variant.featured_image : null)
    if (featured) return catalogMedia(featured, productHandle)

    const handle = productHandle
    const swiper = handle && window.pdp?.swipers?.[handle]?.swiper
    const activeImg =
      swiper?.slides?.[swiper.activeIndex]?.querySelector('img:not(.hidden)') ||
      swiper?.el?.querySelector('.swiper-slide-active img')
    const baseSrc = activeImg?.currentSrc || activeImg?.src || document.getElementById('rx-config-preview-img')?.src
    return baseSrc ? catalogMedia(baseSrc, handle) : null
  }

  function updatePreviewMeta() {
    const title = document.getElementById('rx-config-preview-title')
    const lens = document.getElementById('rx-config-preview-lens')
    const img = document.getElementById('rx-config-preview-img')
    const priceSale = document.getElementById('rx-config-preview-price-sale')
    const priceCompare = document.getElementById('rx-config-preview-price-compare')
    if (title) title.textContent = window.pdp?.currentProduct?.title || ''
    const color = window.pdp?.rx?.['lens-color'] || window.pdp?.selectedVariant?.option2
    if (lens) {
      // Safety has no lens colour, so the fallback label would always read "Clear lens"
      const isSafety = window.pdp?.currentProduct?.product_type === 'Safety'
      lens.hidden = isSafety
      if (isSafety) {
        lens.textContent = ''
      } else if (color) {
        lens.textContent = String(color).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) + ' lens'
      } else {
        lens.textContent = 'Clear lens'
      }
    }
    const { amount, compareAmount } = resolveDisplayedVariantPrices()
    if (priceSale) priceSale.textContent = amount != null ? money(amount) : '—'
    if (priceCompare) {
      if (compareAmount != null && amount != null && compareAmount > amount) {
        priceCompare.textContent = money(compareAmount)
        priceCompare.hidden = false
      } else {
        priceCompare.textContent = ''
        priceCompare.hidden = true
      }
    }
    if (!img) return
    const src = resolvePreviewImageSrc()
    if (src && img.src !== src) img.src = src
  }

  const LENS_COLOR_DISPLAY = {
    clear: 'Clear',
    black: 'Black',
    brown: 'Brown',
    'coastal-blue': 'Coastal-Blue',
    'rose-pink': 'Rose-Pink',
    silver: 'Silver',
    yellow: 'Yellow',
    'green-vibrant': 'Green-Vibrant',
    'purple-pastel': 'Purple-Pastel',
    'photochromic-gray': 'Photochromic-Gray',
    'photochromic-brown': 'Photochromic-Brown'
  }

  function normalizeLensColorHandle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/_/g, '-')
  }

  function catalogMedia(src, handle) {
    if (!src) return null
    if (typeof window.resolveShopifyCdnMediaFromCatalog === 'function') {
      return window.resolveShopifyCdnMediaFromCatalog(src, handle)
    }
    return src
  }

  function productImageSrcList(product) {
    const out = []
    const seen = new Set()
    const push = (src) => {
      if (!src || typeof src !== 'string') return
      if (seen.has(src)) return
      seen.add(src)
      out.push(src)
    }

    const images = product?.images
    if (Array.isArray(images)) {
      images.forEach((im) => push(typeof im === 'string' ? im : im?.src))
    }

    const media = product?.media
    if (Array.isArray(media)) {
      media.forEach((m) => {
        if (m?.media_type && m.media_type !== 'image') return
        push(m?.src || m?.preview_image?.src)
      })
    }

    const variants = product?.variants
    if (Array.isArray(variants)) {
      variants.forEach((v) => {
        push(typeof v?.featured_image === 'string' ? v.featured_image : v?.featured_image?.src)
      })
    }

    return out
  }

  /**
   * Videos as { src: preview image, name: alt }. Shopify renames uploaded videos, so a lens-color
   * video names its color in its alt the way an image does in its filename
   * (e.g. `HUDSON_Nightfall_02_Rx_Photochromic-Gray`).
   */
  function productVideoMediaList(product) {
    const media = product?.media
    if (!Array.isArray(media)) return []
    return media
      .filter((m) => m?.media_type === 'video' && m.alt && (m.src || m.preview_image?.src))
      .map((m) => ({ src: m.src || m.preview_image.src, name: m.alt }))
  }

  function srcMatchesLensColorToken(src, displayToken) {
    if (!src || !displayToken) return false
    const path = String(src).split('?')[0].toLowerCase()
    const file = path.split('/').pop() || ''
    const raw = String(displayToken).toLowerCase().trim()
    const forms = Array.from(
      new Set([
        raw,
        raw.replace(/\s+/g, '-'),
        raw.replace(/\s+/g, '_'),
        raw.replace(/-/g, '_'),
        raw.replace(/_/g, '-'),
        raw.replace(/[-_]+/g, ''),
      ])
    )

    return forms.some((color) => {
      if (!color) return false
      // Exact token after _Rx_ (avoids "brown" matching "photochromic-brown")
      const rxParts = path.split('_rx_')
      if (rxParts.length >= 2) {
        const after = rxParts.pop()
        // Rx filenames are decided by the token alone; the loose fallbacks below would let
        // "brown" match "..._Rx_Photochromic-brown.gif" via "-brown."
        return after === color || after.startsWith(color + '_') || after.startsWith(color + '.')
      }
      return (
        file.includes('_' + color + '_') ||
        file.includes('_' + color + '.') ||
        file.startsWith(color + '_') ||
        file.includes('-' + color + '_') ||
        file.includes('-' + color + '.')
      )
    })
  }

  function isFrontShotSrc(src) {
    if (!src || typeof src !== 'string') return false
    const file = (src.split('?')[0].split('/').pop() || '').toLowerCase()
    return /(?:^|_)01(?:[_.]|$)/.test(file)
  }

  /**
   * Prefer a real catalog file for the lens color — never invent `_01` by rewriting `_02`.
   * Order: matching `_01` → variant featured for that color → any matching color media → null.
   */
  function resolveLensColorProductImageSrc(colorHandle) {
    const handle = normalizeLensColorHandle(colorHandle)
    const display = LENS_COLOR_DISPLAY[handle]
    const product = window.pdp?.currentProduct
    const productHandle = product?.handle
    if (!product || !handle) return null

    const variants = product.variants || []
    const variant = variants.find((v) => normalizeLensColorHandle(v.option2) === handle)
    const variantFeatured =
      (typeof variant?.featured_image === 'string' ? variant.featured_image : variant?.featured_image?.src) || null
    const displayTokens = [
      display,
      variant?.option2,
      String(colorHandle || '').replace(/-/g, ' ')
    ]
      .filter(Boolean)
      .map((t) => String(t).trim())
      // unique, keep Coastal-Blue / Coastal Blue both
      .filter((t, i, arr) => arr.findIndex((x) => x.toLowerCase() === t.toLowerCase()) === i)

    // Prefer richer image list from collection JSON when available
    const catalogProduct =
      (Array.isArray(window.pdp?.mainCollectionProducts) &&
        window.pdp.mainCollectionProducts.find((p) => p.handle === productHandle)) ||
      product
    const catalog = productImageSrcList(catalogProduct).concat(
      catalogProduct !== product ? productImageSrcList(product) : []
    )
    // Images first, then videos (matched on their alt, shown as their preview image)
    const candidates = catalog
      .map((src) => ({ src, name: src }))
      .concat(productVideoMediaList(catalogProduct))
      .concat(catalogProduct !== product ? productVideoMediaList(product) : [])
    const colorMatches = candidates.filter(({ name }) =>
      displayTokens.some((token) => srcMatchesLensColorToken(name, token))
    )

    const front = colorMatches.find(({ name }) => isFrontShotSrc(name))
    if (front) return catalogMedia(front.src, productHandle)

    if (variantFeatured) return catalogMedia(variantFeatured, productHandle)

    if (colorMatches.length) return catalogMedia(colorMatches[0].src, productHandle)

    return null
  }

  /**
   * Safety lens is a native variant option with no per-variant featured image, so the preview has
   * to resolve off the media filename token (CAF / NPAF / PAF / PHOTO) like the PDP gallery does.
   * Without this the preview falls through to the active swiper slide and never leaves Clear.
   */
  function resolveSafetyLensPreviewImageSrc() {
    const product = window.pdp?.currentProduct
    if (product?.product_type !== 'Safety') return null
    if (typeof window.getSafetyLensImageCode !== 'function') return null
    if (typeof window.srcMatchesSafetyLensCode !== 'function') return null

    const code = window.getSafetyLensImageCode(window.pdp?.safetyLensType)
    if (!code) return null

    const productHandle = product.handle
    const catalogProduct =
      (Array.isArray(window.pdp?.mainCollectionProducts) &&
        window.pdp.mainCollectionProducts.find((p) => p.handle === productHandle)) ||
      product
    const catalog = productImageSrcList(catalogProduct).concat(
      catalogProduct !== product ? productImageSrcList(product) : []
    )
    const matches = catalog.filter((src) => window.srcMatchesSafetyLensCode(src, code))
    if (!matches.length) return null

    return catalogMedia(matches.find(isFrontShotSrc) || matches[0], productHandle)
  }

  function syncRxLensColorProductImages() {
    const panel = document.getElementById('lens-color-panel')
    if (!panel) return

    panel.querySelectorAll('.dropdown-option[data-value]').forEach((opt) => {
      let media = opt.querySelector('.rxc-lens-color-media')
      if (!media) {
        media = document.createElement('div')
        media.className = 'rxc-lens-color-media'
        const img = document.createElement('img')
        img.className = 'rxc-lens-color-img'
        img.alt = ''
        img.loading = 'lazy'
        media.appendChild(img)
        opt.insertBefore(media, opt.firstChild)
      }
      const img = media.querySelector('img')
      if (!img) return
      const src = resolveLensColorProductImageSrc(opt.getAttribute('data-value'))
      if (src) {
        if (img.getAttribute('src') !== src) img.src = src
        media.classList.remove('is-empty')
        img.hidden = false
      } else {
        media.classList.add('is-empty')
        img.removeAttribute('src')
        img.hidden = true
      }

      const badge = opt.getAttribute('data-badge')
      const textEl = opt.querySelector('.dropdown-text')
      if (textEl && badge) {
        const price = textEl.textContent.trim()
        const priceLooksLikePrice = /^(FREE|\+?\$)/i.test(price) || price.includes('FREE') || price.includes('$')
        if (priceLooksLikePrice && !textEl.dataset.rxcLabeled) {
          textEl.innerHTML =
            '<span class="rxc-lens-color-name">' +
            badge +
            '</span><span class="rxc-lens-color-price">' +
            price +
            '</span>'
          textEl.dataset.rxcLabeled = '1'
        }
      }
    })
  }

  window.syncRxLensColorProductImages = syncRxLensColorProductImages

  window.updateRxConfigModalPreview = function updateRxConfigModalPreview() {
    updatePreviewMeta()
    syncRxLensColorProductImages()
    // Slider filters/src swaps can finish a tick later
    requestAnimationFrame(() => {
      updatePreviewMeta()
      syncRxLensColorProductImages()
      setTimeout(() => {
        updatePreviewMeta()
        syncRxLensColorProductImages()
      }, 50)
    })
  }

  function summaryRows() {
    const rows = []
    const variant = window.pdp?.selectedVariant
    const price = variant?.price != null ? parseFloat(variant.price) : null
    const type = window.pdp?.currentProduct?.product_type
    if (type === 'Ophthalmic' && window.pdp?.rx?.['oph-type']) {
      rows.push({
        label: 'Type',
        value: String(window.pdp.rx['oph-type']).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        included: false
      })
    }
    if (window.pdp?.rx?.['lens-type'] && document.querySelector('#lens-type-panel .dropdown-option.selected')) {
      rows.push({
        label: 'Lens type',
        value: String(window.pdp.rx['lens-type']).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        included: false
      })
    }
    if (window.pdp?.rx?.['lens-color'] && document.querySelector('#lens-color-panel .dropdown-option.selected')) {
      rows.push({
        label: 'Lens color',
        value: String(window.pdp.rx['lens-color']).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        included: false
      })
    }
    const upgrades = window.pdp?.rx?.['lens-upgrade']
    if (upgrades && typeof upgrades === 'object') {
      Object.keys(upgrades).filter((k) => upgrades[k]).forEach((k) => {
        rows.push({
          label: k.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          value: k === 'standard' ? 'Included' : 'Selected',
          included: k === 'standard'
        })
      })
    }
    if (price != null) {
      rows.unshift({ label: 'Frame / pair', value: money(price), included: false })
    }
    return rows
  }

  function updateSummary() {
    const body = document.getElementById('rx-config-summary-body')
    const total = document.getElementById('rx-config-summary-total')
    const compare = document.getElementById('rx-config-summary-compare')
    const boardTotal = document.getElementById('rx-config-summary-board-total')
    const boardCompare = document.getElementById('rx-config-summary-board-compare')
    const boardRows = document.getElementById('rx-config-summary-board-rows')
    const { amount, compareAmount } = resolveDisplayedVariantPrices()

    const totalText = amount != null ? money(amount) : '—'
    if (total) total.textContent = totalText
    if (boardTotal) boardTotal.textContent = totalText

    const setCompareEl = (el) => {
      if (!el) return
      if (compareAmount != null && amount != null && compareAmount > amount) {
        el.textContent = money(compareAmount)
        el.hidden = false
      } else {
        el.textContent = ''
        el.hidden = true
      }
    }
    setCompareEl(compare)
    setCompareEl(boardCompare)

    const html = summaryRows().map((r) => (
      '<div class="rx-config-modal__summary-row"><span>' + r.label + '</span><span class="' +
      (r.included ? 'is-included' : '') + '">' + r.value + '</span></div>'
    )).join('')
    if (body) body.innerHTML = html
    if (boardRows) boardRows.innerHTML = html
  }

  function setSelectValue(id, value) {
    const el = document.getElementById(id)
    if (!el || value == null || value === '') return
    const str = String(value)
    const opt = Array.from(el.options).find((o) => o.value === str || o.value === str.replace(/^\+/, '') || o.textContent.trim() === str)
    if (opt) el.value = opt.value
    else {
      // try nearest match
      el.value = str
    }
  }

  function setPdSelectClosest(id, value) {
    const select = document.getElementById(id)
    if (!select || value == null || value === '') return
    const n = Number(value)
    if (isNaN(n)) return
    let closest = null
    let minDiff = Infinity
    Array.from(select.options).forEach((opt) => {
      if (!opt.value) return
      const diff = Math.abs(Number(opt.value) - n)
      if (diff < minDiff) {
        minDiff = diff
        closest = opt
      }
    })
    if (closest) {
      select.value = closest.value
      select.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  function applyGlassonPdToRxConfig(detail) {
    if (!detail || detail.pd == null || detail.pd === '') return
    const hasDualPd = Number(detail.leftPd) > 0 && Number(detail.rightPd) > 0
    if (hasDualPd) {
      setPdMode('2')
      setPdSelectClosest('rxc_pd_left', detail.leftPd)
      setPdSelectClosest('rxc_pd_right', detail.rightPd)
    } else {
      setPdMode('1')
      const pdEl = document.getElementById('rxc_pd')
      if (pdEl) {
        const total = Math.round(Number(detail.pd))
        pdEl.value = isNaN(total) ? '' : String(total)
        pdEl.dispatchEvent(new Event('input', { bubbles: true }))
        pdEl.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
    ;['rxc_pd', 'rxc_pd_left', 'rxc_pd_right'].forEach((id) => {
      const err = document.getElementById(id + '-error')
      if (err) {
        err.innerHTML = ''
        err.hidden = true
      }
      const warn = document.getElementById(id + '-warn')
      if (warn) {
        warn.innerHTML = ''
        warn.hidden = true
      }
      document.getElementById(id)?.closest('label')?.classList.remove('has-error')
    })
    updateSoftWarningsLive()
  }

  function readCustomerSavedRx() {
    try {
      const el = document.getElementById('rx-config-saved-customer-rx')
      if (!el) return null
      const data = JSON.parse(el.textContent)
      if (!data || data.right_sphere == null || data.right_sphere === '') return null
      return data
    } catch (e) {
      return null
    }
  }

  function lineHasManualRx(properties) {
    if (!properties) return false
    if (properties._choice === 'SUBMIT LATER' && !properties._right_sphere) return false
    const required = ['_right_sphere', '_right_cylinder', '_right_axis', '_left_sphere', '_left_cylinder', '_left_axis']
    return required.every((k) => properties[k] != null && String(properties[k]).trim() !== '')
  }

  async function fetchCartRxProps() {
    try {
      const res = await fetch('/cart.js')
      const cart = await res.json()
      const hit = (cart.items || []).find((item) => {
        const title = String(item.variant_title || item.title || '')
        const rxLine =
          item.product_type === 'Prescription' ||
          (item.product_type === 'Ophthalmic' && title.includes('Prescription / '))
        return rxLine && lineHasManualRx(item.properties)
      })
      return hit ? hit.properties : null
    } catch (e) {
      return null
    }
  }

  function setSameAsBeforeUi(on) {
    state.sameAsBefore = !!on
    const btn = document.getElementById('rx-config-same-as-before-wrap')
    if (!btn) return
    btn.classList.toggle('is-selected', state.sameAsBefore)
    btn.setAttribute('aria-pressed', state.sameAsBefore ? 'true' : 'false')
  }

  async function prepareSameAsBefore() {
    const wrap = document.getElementById('rx-config-same-as-before-wrap')
    if (!wrap) return
    if (!state.cartRxProps) state.cartRxProps = await fetchCartRxProps()
    const customerRx = readCustomerSavedRx()
    const has = !!(state.cartRxProps || customerRx)
    wrap.hidden = !has
    if (!has) setSameAsBeforeUi(false)
  }

  function propsFromFormToFillSource() {
    if (state.cartRxProps) return state.cartRxProps
    const c = readCustomerSavedRx()
    if (!c) return null
    return {
      _right_sphere: c.right_sphere,
      _right_cylinder: c.right_cylinder,
      _right_axis: c.right_axis,
      _right_correction: c.right_add,
      _left_sphere: c.left_sphere,
      _left_cylinder: c.left_cylinder,
      _left_axis: c.left_axis,
      _left_correction: c.left_add,
      _pd: c.pd_left && c.pd_right && !c.pd ? '2' : '1',
      _pd_left: c.pd_left || c.pd,
      _pd_right: c.pd_right || '',
      _date_issued: c.date_issued,
      _date_expire: c.date_expire,
      _length_of_rx: '',
      _upload: c.rx_file_url
    }
  }

  function fillFormFromProps(props) {
    if (!props) return
    setSelectValue('rxc_right_sphere', props._right_sphere)
    setSelectValue('rxc_right_cylinder', props._right_cylinder)
    setSelectValue('rxc_right_axis', props._right_axis)
    setSelectValue('rxc_left_sphere', props._left_sphere)
    setSelectValue('rxc_left_cylinder', props._left_cylinder)
    setSelectValue('rxc_left_axis', props._left_axis)
    setSelectValue('rxc_add', props._right_correction || props._left_correction)
    if (props._date_issued) {
      const d = document.getElementById('rxc_date_issued')
      if (d) d.value = String(props._date_issued).slice(0, 10)
    }
    let lengthYears = props._length_of_rx
    if (!lengthYears && props._date_issued && props._date_expire) {
      try {
        const a = new Date(String(props._date_issued).slice(0, 10))
        const b = new Date(String(props._date_expire).slice(0, 10))
        const years = Math.round((b - a) / (365.25 * 24 * 3600 * 1000))
        if (years === 1 || years === 2) lengthYears = String(years)
      } catch (e) { /* ignore */ }
    }
    setSelectValue('rxc_length_of_rx', lengthYears || '1')
    computeExpireFromIssueAndLength()
    const pdMode = String(props._pd) === '2' ? '2' : '1'
    setPdMode(pdMode)
    if (pdMode === '1') {
      const pdEl = document.getElementById('rxc_pd')
      const total = singlePdTotalFromProps(props)
      if (pdEl && total !== '') pdEl.value = total
    } else {
      setSelectValue('rxc_pd_left', props._pd_left)
      setSelectValue('rxc_pd_right', props._pd_right)
    }
    if (props._upload) {
      state.uploadUrl = props._upload
      state.uploadName = 'Saved prescription'
    }
    setAdditionalInfoFromProps(props)
  }

  function stripAdditionalInfoHtml(html) {
    const tmp = document.createElement('div')
    tmp.innerHTML = html == null ? '' : String(html)
    return (tmp.textContent || tmp.innerText || '').slice(0, 200)
  }

  function setAdditionalInfoOpen(open) {
    const toggle = document.getElementById('rxc-additional-toggle')
    const body = document.getElementById('rxc-additional-body')
    if (toggle) {
      toggle.classList.toggle('is-active', !!open)
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
    }
    if (body) body.hidden = !open
  }

  function syncAdditionalInfoCount() {
    const ta = document.getElementById('rxc_additional_data')
    const count = document.getElementById('rxc_additional_data_count')
    if (count) count.textContent = String((ta?.value || '').length)
  }

  function setAdditionalInfoFromProps(props) {
    const ta = document.getElementById('rxc_additional_data')
    if (!ta) return
    const raw = props && props._additional_data_text != null
      ? String(props._additional_data_text)
      : ''
    const text = stripAdditionalInfoHtml(raw).trim()
    ta.value = text
    syncAdditionalInfoCount()
    setAdditionalInfoOpen(!!text)
  }

  function clearAdditionalInfo() {
    const ta = document.getElementById('rxc_additional_data')
    if (ta) ta.value = ''
    syncAdditionalInfoCount()
    setAdditionalInfoOpen(false)
  }

  function readAdditionalInfoProperty() {
    const ta = document.getElementById('rxc_additional_data')
    const toggle = document.getElementById('rxc-additional-toggle')
    const open = toggle?.classList.contains('is-active') || (toggle?.getAttribute('aria-expanded') === 'true')
    if (!open || !ta) return ''
    return stripAdditionalInfoHtml(ta.value).trim()
  }

  function setPdMode(mode) {
    state.pdMode = mode
    document.querySelectorAll('[data-pd-mode]').forEach((btn) => {
      btn.classList.toggle('is-selected', btn.getAttribute('data-pd-mode') === mode)
    })
    document.querySelectorAll('[data-pd-fields]').forEach((el) => {
      el.hidden = el.getAttribute('data-pd-fields') !== mode
    })
    updateSoftWarningsLive()
  }

  function syncUploadUi(mode) {
    // mode: 'empty' | 'busy' | 'ready'
    const uploader = document.getElementById('rx-config-uploader')
    const trigger = document.getElementById('rx-config-upload-trigger')
    const previewBtn = document.getElementById('rx-config-upload-preview')
    const nameEl = document.getElementById('rx-config-upload-name')
    const note = document.getElementById('rx-config-ocr-note')
    const hint = document.getElementById('rx-config-upload-hint')
    if (!uploader || !trigger) return

    const hasFile = !!state.uploadUrl
    const resolved = mode || (hasFile ? 'ready' : 'empty')

    uploader.classList.toggle('is-busy', resolved === 'busy')
    uploader.classList.toggle('is-compact', resolved === 'ready' || (resolved === 'busy' && hasFile))

    if (resolved === 'empty') {
      trigger.textContent = 'Choose file'
      trigger.disabled = false
      if (previewBtn) previewBtn.hidden = true
      if (nameEl) {
        nameEl.textContent = ''
        nameEl.hidden = true
      }
      if (note) {
        note.textContent = ''
        note.hidden = true
      }
      if (hint) hint.hidden = false
      return
    }

    if (resolved === 'busy') {
      trigger.textContent = hasFile ? 'Change file' : 'Choose file'
      trigger.disabled = true
      if (previewBtn) previewBtn.hidden = !hasFile
      if (nameEl) {
        nameEl.textContent = state.uploadName || 'Uploading…'
        nameEl.hidden = false
      }
      if (note) {
        note.hidden = false
        if (!note.textContent) note.textContent = 'Uploading prescription…'
      }
      if (hint) hint.hidden = true
      return
    }

    // ready
    trigger.textContent = 'Change file'
    trigger.disabled = false
    if (previewBtn) previewBtn.hidden = !hasFile
    if (nameEl) {
      nameEl.textContent = state.uploadName || 'Uploaded prescription'
      nameEl.hidden = false
    }
    if (hint) hint.hidden = true
  }

  function setRxMethod(method) {
    state.rxMethod = method || ''
    state.sendLater = method === 'send-later'

    const root = document.getElementById('rx-config-rx-step')
    if (root) root.classList.toggle('is-send-later', state.sendLater)

    document.querySelectorAll('[data-rx-method]').forEach((btn) => {
      btn.classList.toggle('is-selected', !!method && btn.getAttribute('data-rx-method') === method)
    })

    const uploadPanel = document.querySelector('[data-rx-panel="upload"]')
    const manualPanel = document.querySelector('[data-rx-panel="manual"]')

    if (!method) {
      if (uploadPanel) uploadPanel.hidden = true
      if (manualPanel) manualPanel.hidden = true
      return
    }

    if (method === 'send-later') {
      if (uploadPanel) uploadPanel.hidden = true
      if (manualPanel) manualPanel.hidden = true
      return
    }

    if (method === 'upload') {
      if (!state.uploadUrl) state.forceShowManual = false
      if (uploadPanel) uploadPanel.hidden = false
      // After OCR (or OCR fail): also open manual form under Enter manually
      // Confirm checkbox lives inside the manual panel
      if (manualPanel) manualPanel.hidden = !(state.ocrFilled || state.forceShowManual)
      syncUploadUi(state.uploadUrl ? 'ready' : 'empty')
      return
    }

    // manual accordion: form between Enter manually and Send later
    state.forceShowManual = false
    if (uploadPanel) uploadPanel.hidden = true
    if (manualPanel) manualPanel.hidden = false
  }

  function isProgressiveLensSelected() {
    const rxLt = String(window.pdp?.rx?.['lens-type'] || '').toLowerCase()
    if (rxLt.includes('progressive')) return true
    const v = window.pdp?.selectedVariant
    if (!v) return false
    const bits = [v.option1, v.option2, v.option3, v.title]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return bits.includes('progressive')
  }

  function syncAddPowerVisibility() {
    const block = document.getElementById('rx-config-add-power')
    if (!block) return
    const progressive = isProgressiveLensSelected()
    block.hidden = !progressive
    if (!progressive) {
      const add = document.getElementById('rxc_add')
      if (add) add.value = ''
    }
  }

  function val(id) {
    return document.getElementById(id)?.value || ''
  }

  function num(id) {
    const v = val(id)
    if (v === '') return NaN
    return parseFloat(v)
  }

  function hasHighIndexUpgrade() {
    const upgrades = window.pdp?.rx?.['lens-upgrade']
    return !!(upgrades && upgrades['high-index'])
  }

  function setFieldMessage(id, message, kind) {
    const el = document.getElementById(id + (kind === 'warn' ? '-warn' : '-error'))
    const input = document.getElementById(id)
    const label = input?.closest('label')
    if (!el) return
    if (message) {
      el.innerHTML = message
      el.hidden = false
      if (label) {
        label.classList.toggle('has-error', kind !== 'warn')
        label.classList.toggle('has-warn', kind === 'warn')
      }
    } else {
      el.innerHTML = ''
      el.hidden = true
      if (label && kind !== 'warn') label.classList.remove('has-error')
      if (label && kind === 'warn') label.classList.remove('has-warn')
    }
  }

  function clearRxFieldMessages() {
    document.querySelectorAll('.rx-config-rx__field-error, .rx-config-rx__field-warn').forEach((el) => {
      el.innerHTML = ''
      el.hidden = true
    })
    document.querySelectorAll('.rx-config-rx label.has-error, .rx-config-rx label.has-warn').forEach((el) => {
      el.classList.remove('has-error', 'has-warn')
    })
  }

  function toastValidationFailed(errors) {
    if (typeof openErrorsToast === 'function') {
      openErrorsToast('Validation Failed', { errors: errors })
      return
    }
    const first = Object.keys(errors).map((k) => errors[k]).find(Boolean)
    alert(first ? String(first).replace(/<[^>]+>/g, '') : 'Validation Failed')
  }

  function pdTotalFromInputs() {
    if (state.pdMode === '1') return num('rxc_pd')
    const left = num('rxc_pd_left')
    const right = num('rxc_pd_right')
    if (isNaN(left) || isNaN(right)) return NaN
    return left + right
  }

  function singlePdTotalFromProps(props) {
    const left = parseFloat(props._pd_left)
    const right = parseFloat(props._pd_right)
    if (!isNaN(left) && !isNaN(right) && props._pd_right !== '' && props._pd_right != null) {
      return String(left + right)
    }
    if (!isNaN(left)) return String(left)
    return ''
  }

  function collectSoftWarnings() {
    const warnings = {}
    const rs = num('rxc_right_sphere')
    const ls = num('rxc_left_sphere')
    const rc = num('rxc_right_cylinder')
    const lc = num('rxc_left_cylinder')

    if (!isNaN(rs) && !isNaN(ls) && Math.abs(ls - rs) > 4) {
      warnings.sphereDelta = 'We noticed a greater than average variation in values between OD & OS Spheres'
    }
    if (!isNaN(rc) && !isNaN(lc) && Math.abs(lc - rc) > 4) {
      warnings.cylDelta = 'We noticed a greater than average variation in values between OD & OS Cylinders'
    }

    const pdSum = pdTotalFromInputs()
    if (!isNaN(pdSum) && ((pdSum >= 30 && pdSum < 57) || (pdSum <= 80 && pdSum > 68))) {
      warnings.pdAvg = 'We noticed your PD is outside the average range of 57 - 68'
    }

    return warnings
  }

  function renderSoftWarnings(warnings) {
    ;['rxc_right_sphere', 'rxc_left_sphere', 'rxc_right_cylinder', 'rxc_left_cylinder', 'rxc_pd', 'rxc_pd_left', 'rxc_pd_right'].forEach((id) => {
      setFieldMessage(id, '', 'warn')
    })

    const byField = {}
    const append = (id, msg) => {
      if (!byField[id]) byField[id] = []
      if (!byField[id].includes(msg)) byField[id].push(msg)
    }

    if (warnings.sphereDelta) {
      append('rxc_right_sphere', warnings.sphereDelta)
      append('rxc_left_sphere', warnings.sphereDelta)
    }
    if (warnings.cylDelta) {
      append('rxc_right_cylinder', warnings.cylDelta)
      append('rxc_left_cylinder', warnings.cylDelta)
    }
    if (warnings.pdAvg) {
      if (state.pdMode === '1') append('rxc_pd', warnings.pdAvg)
      else {
        append('rxc_pd_left', warnings.pdAvg)
        append('rxc_pd_right', warnings.pdAvg)
      }
    }

    Object.keys(byField).forEach((id) => {
      setFieldMessage(id, byField[id].join('<br>'), 'warn')
    })
  }

  function updateSoftWarningsLive() {
    if (state.sendLater) return
    renderSoftWarnings(collectSoftWarnings())
  }

  function computeExpireFromIssueAndLength() {
    const issuedEl = document.getElementById('rxc_date_issued')
    const expireEl = document.getElementById('rxc_date_expire')
    const lengthEl = document.getElementById('rxc_length_of_rx')
    if (!issuedEl || !expireEl || !lengthEl) return
    const issued = (issuedEl.value || '').trim()
    const years = parseInt(lengthEl.value, 10)
    if (!issued || isNaN(years) || years < 1) {
      expireEl.value = ''
      return
    }
    const d = new Date(issued)
    if (isNaN(d.getTime())) {
      expireEl.value = ''
      return
    }
    d.setFullYear(d.getFullYear() + years)
    expireEl.value = d.toISOString().slice(0, 10)
  }

  function bindRxDateFields() {
    const issuedEl = document.getElementById('rxc_date_issued')
    const lengthEl = document.getElementById('rxc_length_of_rx')
    if (issuedEl && !issuedEl.dataset.rxDateBound) {
      issuedEl.dataset.rxDateBound = '1'
      const today = new Date()
      const threeYearsAgo = new Date()
      threeYearsAgo.setFullYear(today.getFullYear() - 3)
      issuedEl.setAttribute('min', threeYearsAgo.toISOString().slice(0, 10))
      issuedEl.setAttribute('max', today.toISOString().slice(0, 10))
      issuedEl.addEventListener('change', computeExpireFromIssueAndLength)
      issuedEl.addEventListener('input', computeExpireFromIssueAndLength)
    }
    if (lengthEl && !lengthEl.dataset.rxDateBound) {
      lengthEl.dataset.rxDateBound = '1'
      lengthEl.addEventListener('change', computeExpireFromIssueAndLength)
    }
  }

  function attachUploadProperty(props) {
    // Always persist uploaded prescription file on the Shopify line item.
    if (state.uploadUrl) {
      props._upload = state.uploadUrl
    }
    return props
  }

  function collectRxProperties() {
    if (state.sendLater) {
      return attachUploadProperty({ _choice: 'SUBMIT LATER' })
    }

    // "Same as before" only prefills. Submit uses current form values
    // (saved RX + any edits / newly uploaded file).
    const props = {
      _choice: 'ENTER MANUALLY',
      _right_sphere: val('rxc_right_sphere'),
      _right_cylinder: val('rxc_right_cylinder'),
      _right_axis: val('rxc_right_axis'),
      _left_sphere: val('rxc_left_sphere'),
      _left_cylinder: val('rxc_left_cylinder'),
      _left_axis: val('rxc_left_axis'),
      _pd: state.pdMode,
      _date_issued: val('rxc_date_issued'),
      _date_expire: val('rxc_date_expire'),
      _length_of_rx: val('rxc_length_of_rx') || '1',
      _save_rx_info: 'true'
    }

    if (state.pdMode === '1') {
      const total = num('rxc_pd')
      // Match standalone RX form: store half PD per eye for single PD
      props._pd_left = isNaN(total) ? '' : String(total / 2)
      props._pd_right = isNaN(total) ? '' : String(total / 2)
    } else {
      props._pd_left = val('rxc_pd_left')
      props._pd_right = val('rxc_pd_right')
    }

    const add = val('rxc_add')
    if (add) {
      props._right_correction = add
      props._left_correction = add
    }

    computeExpireFromIssueAndLength()
    props._date_expire = val('rxc_date_expire')

    const additional = readAdditionalInfoProperty()
    if (additional) {
      props._additional_data_text = additional
    } else {
      props._additional_data_text = ''
    }

    return attachUploadProperty(props)
  }

  function validateRxStep() {
    if (state.sendLater) {
      clearRxFieldMessages()
      return true
    }

    clearRxFieldMessages()
    const errors = {}
    let hasError = false

    const required = [
      ['rxc_right_sphere', 'rightSphere', '<b>Right sphere</b> value(OD) is required'],
      ['rxc_right_cylinder', 'rightCylinder', '<b>Right cylinder</b> value(OD) is required'],
      ['rxc_right_axis', 'rightAxis', '<b>Right axis</b> value(OD) is required'],
      ['rxc_left_sphere', 'leftSphere', '<b>Left sphere</b> value(OS) is required'],
      ['rxc_left_cylinder', 'leftCylinder', '<b>Left cylinder</b> value(OS) is required'],
      ['rxc_left_axis', 'leftAxis', '<b>Left axis</b> value(OS) is required'],
      ['rxc_date_issued', 'dateIssued', '<b>Issue date</b> is required'],
      ['rxc_length_of_rx', 'lengthOfRx', '<b>Rx Valid For</b> is required']
    ]

    required.forEach(([id, key, msg]) => {
      if (!val(id)) {
        hasError = true
        errors[key] = msg
        setFieldMessage(id, 'This field is required', 'error')
      }
    })

    if (isProgressiveLensSelected()) {
      const add = val('rxc_add')
      if (!add) {
        hasError = true
        errors.correction = '<b>ADD</b> value is required'
        setFieldMessage('rxc_add', 'This field is required', 'error')
      }
    }

    if (state.pdMode === '1') {
      const pd = num('rxc_pd')
      if (val('rxc_pd') === '') {
        hasError = true
        errors.onePD = '<b>PD</b> value is required'
        setFieldMessage('rxc_pd', 'This field is required', 'error')
      } else if (pd < 54 || pd > 73) {
        hasError = true
        errors.onePD = '<b>PD</b> is outside the allowed range'
        setFieldMessage('rxc_pd', 'Total <b>PD</b> must be between 54 and 73', 'error')
      }
    } else {
      const rightPd = val('rxc_pd_right')
      const leftPd = val('rxc_pd_left')
      const rightN = parseFloat(rightPd)
      const leftN = parseFloat(leftPd)
      const pdSum = rightN + leftN
      const perEyeOutOfRange =
        (rightPd !== '' && (rightN < 27 || rightN > 37)) ||
        (leftPd !== '' && (leftN < 27 || leftN > 37))

      if (rightPd === '') {
        hasError = true
        errors.rightPD = '<b>Right PD</b> value(OD) is required'
        setFieldMessage('rxc_pd_right', 'This field is required', 'error')
      } else if (perEyeOutOfRange || pdSum < 54 || pdSum > 74) {
        hasError = true
        errors.rightPD = '<b>PD</b> is outside the allowed range'
        setFieldMessage('rxc_pd_right', 'Each eye <b>PD</b> must be between 27 and 37', 'error')
      }

      if (leftPd === '') {
        hasError = true
        errors.leftPD = '<b>Left PD</b> value(OS) is required'
        setFieldMessage('rxc_pd_left', 'This field is required', 'error')
      } else if (perEyeOutOfRange || pdSum < 54 || pdSum > 74) {
        hasError = true
        errors.leftPD = '<b>PD</b> is outside the allowed range'
        setFieldMessage('rxc_pd_left', 'Each eye <b>PD</b> must be between 27 and 37', 'error')
      }
    }

    const rightCyl = num('rxc_right_cylinder')
    const rightAxis = num('rxc_right_axis')
    const leftCyl = num('rxc_left_cylinder')
    const leftAxis = num('rxc_left_axis')

    if (!isNaN(rightCyl) && !isNaN(rightAxis)) {
      if (rightCyl !== 0 && rightAxis === 0) {
        hasError = true
        errors.rightAxis = '<b>Right Axis</b> should not be 0 when&nbsp;<b>Right Cylinder</b> has a value'
        setFieldMessage('rxc_right_axis', errors.rightAxis, 'error')
      }
      if (rightAxis !== 0 && rightAxis !== 180 && rightCyl === 0) {
        hasError = true
        errors.rightAxis = '<b>Right Cylinder</b> should not be 0 when&nbsp;<b>Right Axis</b> has a value'
        setFieldMessage('rxc_right_cylinder', errors.rightAxis, 'error')
      }
    }

    if (!isNaN(leftCyl) && !isNaN(leftAxis)) {
      if (leftCyl !== 0 && leftAxis === 0) {
        hasError = true
        errors.leftAxis = '<b>Left Axis</b> should not be 0 when&nbsp;<b>Left Cylinder</b> has a value'
        setFieldMessage('rxc_left_axis', errors.leftAxis, 'error')
      }
      if (leftAxis !== 0 && leftAxis !== 180 && leftCyl === 0) {
        hasError = true
        errors.leftAxis = '<b>Left Cylinder</b> should not be 0 when&nbsp;<b>Left Axis</b> has a value'
        setFieldMessage('rxc_left_cylinder', errors.leftAxis, 'error')
      }
    }

    if (!isNaN(rightCyl) && !isNaN(leftCyl) && rightCyl !== 0 && leftCyl !== 0 && rightCyl * leftCyl < 0) {
      hasError = true
      errors.cylSign = 'OD and OS Cylinder values must both be positive or both be negative.'
      setFieldMessage('rxc_right_cylinder', errors.cylSign, 'error')
      setFieldMessage('rxc_left_cylinder', errors.cylSign, 'error')
    }

    computeExpireFromIssueAndLength()
    const issued = val('rxc_date_issued')
    const expire = val('rxc_date_expire')
    if (issued && !expire) {
      hasError = true
      errors.dateExpire = '<b>Expire date</b> could not be calculated from Issue Date and Rx Valid For'
      setFieldMessage('rxc_length_of_rx', 'Please select a valid Rx length', 'error')
    }

    const rightTotal = (num('rxc_right_sphere') || 0) + (isNaN(rightCyl) ? 0 : rightCyl)
    const leftTotal = (num('rxc_left_sphere') || 0) + (isNaN(leftCyl) ? 0 : leftCyl)
    if (
      !hasHighIndexUpgrade() &&
      val('rxc_right_sphere') !== '' &&
      val('rxc_left_sphere') !== '' &&
      (rightTotal < -5 || rightTotal > 6 || leftTotal < -5 || leftTotal > 6)
    ) {
      hasError = true
      errors.overall = 'Your prescription requires High Index lenses. Please select High Index or contact support.'
      if (rightTotal < -5 || rightTotal > 6) {
        errors.rightTotalRange = `Right eye total (Sphere + Cylinder = ${rightTotal.toFixed(2)}) is outside the standard range (-5 to +6).`
        setFieldMessage('rxc_right_sphere', errors.rightTotalRange, 'error')
      }
      if (leftTotal < -5 || leftTotal > 6) {
        errors.leftTotalRange = `Left eye total (Sphere + Cylinder = ${leftTotal.toFixed(2)}) is outside the standard range (-5 to +6).`
        setFieldMessage('rxc_left_sphere', errors.leftTotalRange, 'error')
      }
    }

    const confirm = document.getElementById('rx-config-rx-confirm')
    if (confirm && !confirm.checked) {
      hasError = true
      errors.confirm = 'Please confirm your prescription is accurate.'
      setFieldMessage('rx-config-rx-confirm', 'This field is required', 'error')
    }

    const softWarnings = collectSoftWarnings()
    renderSoftWarnings(softWarnings)

    if (hasError) {
      toastValidationFailed(errors)
      const firstErr = document.querySelector('.rx-config-rx__field-error:not([hidden])')
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      return false
    }

    return true
  }

  function canLeaveCurrentStep() {
    const step = currentStepId()
    if (step === 'rx-entry') return validateRxStep()
    return stepHasRequiredSelection()
  }

  function goTo(index) {
    state.steps = buildSteps()
    if (index < 0) index = 0
    if (index >= state.steps.length) index = state.steps.length - 1
    state.index = index
    syncStepVisibility()
  }

  function rebuildAndStay() {
    const prev = currentStepId()
    state.steps = buildSteps()
    let nextIndex = state.steps.indexOf(prev)
    if (nextIndex < 0) nextIndex = Math.min(state.index, state.steps.length - 1)
    state.index = Math.max(0, nextIndex)
    syncStepVisibility()
  }

  function setFinalAtcLoading(loading, label) {
    const continueBtn = document.getElementById('rx-config-continue')
    if (!continueBtn) return
    if (loading) {
      if (continueBtn.dataset.loading === '1') return
      continueBtn.dataset.loading = '1'
      continueBtn.dataset.label = continueBtn.textContent || 'Add to cart'
      continueBtn.disabled = true
      continueBtn.innerHTML = '<div class="spinning_circle"></div>'
      return
    }
    continueBtn.dataset.loading = '0'
    continueBtn.textContent = label || continueBtn.dataset.label || 'Add to cart'
    syncContinueEnabledState()
  }

  function toastAtcBlocked(message) {
    if (typeof openErrorsToast === 'function') {
      openErrorsToast('Unable to add to cart', {
        errors: { message: message || 'Please select a lens type and upgrade, then try again.' }
      })
      return
    }
    console.warn('[rx config] ATC blocked:', message)
  }

  function triggerAtc() {
    window.pdp = window.pdp || {}
    const continueBtn = document.getElementById('rx-config-continue')
    if (continueBtn?.dataset.loading === '1') return

    // Coatings without Standard/IR/HI never resolve a Shopify variant
    ensureBaseLensUpgrade()

    // Resolve variant before clicking the hidden ATC (Storefront-safe)
    if (typeof window.pdpFindSelectedVariant === 'function') {
      try { window.pdpFindSelectedVariant() } catch (e) {}
    }

    if (!window.pdp.selectedVariant?.id) {
      setFinalAtcLoading(false, 'Add to cart')
      toastAtcBlocked(
        hasBaseLensUpgradeSelected()
          ? 'This lens combination is unavailable. Try a different color or upgrade.'
          : 'Choose Standard, Impact Resistant, or High Index before adding to cart.'
      )
      syncContinueEnabledState()
      return
    }

    const atcRoot = document.querySelector('#rx-config-modal .rx-config-modal__atc')
    const atcBtn = atcRoot?.querySelector('.atc-button')
    if (!atcBtn) {
      setFinalAtcLoading(false, 'Add to cart')
      toastAtcBlocked('Add to cart is temporarily unavailable. Please refresh and try again.')
      return
    }
    // updateATCText may leave sticky ATC disabled ("SELECT OPTIONS") even after upgrade pick
    if (atcBtn.classList.contains('disabled')) {
      if (window.pdp.selectedVariant?.id && !/sold out/i.test(atcBtn.textContent || '')) {
        atcBtn.classList.remove('disabled')
      } else {
        setFinalAtcLoading(false, 'Add to cart')
        toastAtcBlocked(
          /sold out/i.test(atcBtn.textContent || '')
            ? 'This option is sold out. Choose a different lens combination.'
            : 'Finish selecting your lens options before adding to cart.'
        )
        return
      }
    }
    setFinalAtcLoading(true)
    atcRoot.hidden = false
    atcRoot.style.position = 'absolute'
    atcRoot.style.width = '1px'
    atcRoot.style.height = '1px'
    atcRoot.style.overflow = 'hidden'
    atcRoot.style.clip = 'rect(0 0 0 0)'
    try {
      atcBtn.click()
    } catch (err) {
      console.error('[rx config] ATC click failed', err)
      setFinalAtcLoading(false, 'Add to cart')
      return
    }
    // Failsafe: never leave the Continue button spinning forever
    clearTimeout(window.__rxAtcFailsafe)
    window.__rxAtcFailsafe = setTimeout(() => {
      const btn = document.getElementById('rx-config-continue')
      if (btn && btn.dataset.loading === '1') setFinalAtcLoading(false, 'Add to cart')
    }, 12000)
  }

  async function uploadRxFile(file) {
    const note = document.getElementById('rx-config-ocr-note')
    state.uploadName = file.name
    state.uploadUrl = ''
    state.ocrFilled = false
    state.forceShowManual = false
    if (note) note.textContent = 'Uploading prescription…'
    syncUploadUi('busy')

    const body = new FormData()
    body.append('file', file)
    try {
      const res = await fetch('https://api.neveneyewear.com/api/upload/image', {
        method: 'POST',
        body
      })
      const data = await res.json().catch(() => ({}))
      // API returns { success: "success", data: { imageUrl: "uploads/..." } }
      const path =
        data?.data?.imageUrl ||
        data?.imageUrl ||
        data?.url ||
        data?.path ||
        data?.data?.url ||
        data?.data?.path
      if (!res.ok || !path) throw new Error('Upload failed')
      state.uploadUrl = path.startsWith('http') ? path : ('https://api.neveneyewear.com/' + String(path).replace(/^\//, ''))
      state.uploadName = file.name
      if (note) note.textContent = 'Reading prescription…'
      syncUploadUi('busy')
      await runOcr()
    } catch (err) {
      console.error(err)
      state.uploadUrl = ''
      state.uploadName = ''
      state.ocrFilled = false
      const fileInput = document.getElementById('rx-config-rx-file')
      if (fileInput) fileInput.value = ''
      syncUploadUi('empty')
      if (typeof openErrorsToast === 'function') {
        openErrorsToast('Upload failed', {
          errors: { message: 'Please try again or enter values manually.' }
        })
      }
    }
  }

  async function runOcr() {
    if (!state.uploadUrl) return
    const note = document.getElementById('rx-config-ocr-note')
    try {
      if (note) {
        note.hidden = false
        note.textContent = 'Reading prescription…'
      }
      syncUploadUi('busy')
      const res = await fetch('https://api.neveneyewear.com/api/rx-ocr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: state.uploadUrl, imageUrl: state.uploadUrl })
      })
      if (!res.ok) throw new Error('OCR failed')
      const payload = await res.json()
      const ocr = payload?.data ?? payload
      const getVal = (obj, ...keys) => {
        for (const k of keys) {
          if (obj && obj[k] != null && obj[k] !== '') return obj[k]
        }
        return null
      }
      const normalizeQuarter = (v) => {
        const n = parseFloat(v)
        if (isNaN(n)) return null
        const rounded = Math.round(n * 4) / 4
        let s = rounded.toFixed(2)
        if (s.endsWith('0')) s = s.slice(0, -1)
        return s
      }
      const normalizeAxis = (v) => {
        const n = parseFloat(v)
        if (isNaN(n)) return null
        return String(Math.round(n))
      }
      fillFormFromProps({
        _right_sphere: normalizeQuarter(getVal(ocr, 'right_sphere', 'od_sphere', 'OD_SPH', 'odSph')),
        _right_cylinder: normalizeQuarter(getVal(ocr, 'right_cylinder', 'od_cylinder', 'OD_CYL', 'odCyl')),
        _right_axis: normalizeAxis(getVal(ocr, 'right_axis', 'od_axis', 'OD_AXIS', 'odAxis')),
        _left_sphere: normalizeQuarter(getVal(ocr, 'left_sphere', 'os_sphere', 'OS_SPH', 'osSph')),
        _left_cylinder: normalizeQuarter(getVal(ocr, 'left_cylinder', 'os_cylinder', 'OS_CYL', 'osCyl')),
        _left_axis: normalizeAxis(getVal(ocr, 'left_axis', 'os_axis', 'OS_AXIS', 'osAxis')),
        _right_correction: normalizeQuarter(getVal(ocr, 'right_add', 'od_add', 'OD_ADD', 'left_add', 'os_add', 'OS_ADD')),
        _left_correction: normalizeQuarter(getVal(ocr, 'right_add', 'od_add', 'OD_ADD', 'left_add', 'os_add', 'OS_ADD')),
        _pd: getVal(ocr, 'pd_left') && getVal(ocr, 'pd_right') ? '2' : '1',
        _pd_left: normalizeQuarter(getVal(ocr, 'pd', 'pd_left', 'PD')),
        _pd_right: normalizeQuarter(getVal(ocr, 'pd_right'))
      })
      state.ocrFilled = true
      state.forceShowManual = false
      state.ocrPendingConfirm = false
      if (note) {
        note.hidden = false
        note.textContent = 'Fields filled below — please recheck before continuing.'
      }
      syncUploadUi('ready')
      setRxMethod('upload')
      syncAddPowerVisibility()
      updateSoftWarningsLive()
      if (typeof openSuccessToast === 'function') {
        openSuccessToast('Success', {
          errors: {
            message: 'Successfully read the prescription and filled in the fields. Please check if the information is correct.'
          }
        })
      }
    } catch (e) {
      console.error(e)
      state.ocrFilled = false
      state.forceShowManual = true
      if (note) {
        note.hidden = false
        note.textContent = 'Could not read prescription. Enter values manually below.'
      }
      syncUploadUi('ready')
      setRxMethod('upload')
      if (typeof openErrorsToast === 'function') {
        openErrorsToast('Could not read prescription', {
          errors: { message: 'Please enter your prescription values manually.' }
        })
      }
    }
  }

  function isGlassonTryOnAvailable() {
    if (typeof window.openTryOn !== 'function') return false
    if (!document.querySelector('glasson-try-on')) return false
    const wraps = Array.from(document.querySelectorAll('.glasson-wrap'))
    if (!wraps.length) return true
    return wraps.some((wrap) => {
      if (wrap.closest('#rx-config-modal')) return false
      const style = window.getComputedStyle(wrap)
      return style.display !== 'none' && style.visibility !== 'hidden'
    })
  }

  function syncRxConfigTryOnButton() {
    const btn = document.getElementById('rx-config-try-on')
    if (!btn) return
    btn.hidden = !isGlassonTryOnAvailable()
  }

  function bindOnce() {
    const m = modal()
    if (!m || m.dataset.rxStepsBound === '1') return
    m.dataset.rxStepsBound = '1'

    document.getElementById('rx-config-back')?.addEventListener('click', () => {
      goTo(state.index - 1)
    })

    document.getElementById('rx-config-continue')?.addEventListener('click', () => {
      const continueBtn = document.getElementById('rx-config-continue')
      if (continueBtn?.dataset.loading === '1' || continueBtn?.disabled) return
      if (!canLeaveCurrentStep()) return
      const step = currentStepId()
      if (step === 'oph-type') {
        state.steps = buildSteps()
        if (isOphBlueLightOrPlain()) {
          window.pdp.rxConfigLineProperties = null
          triggerAtc()
          return
        }
        goTo(1)
        return
      }
      if (continueBtn?.dataset.final === '1') {
        if (step === 'rx-entry') {
          window.pdp.rxConfigLineProperties = collectRxProperties()
        } else {
          window.pdp.rxConfigLineProperties = null
        }
        triggerAtc()
        return
      }
      goTo(state.index + 1)
    })

    // Rx entry step controls
    m.querySelectorAll('[data-rx-method]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const method = btn.getAttribute('data-rx-method') || 'manual'
        if (method === 'send-later') {
          setSameAsBeforeUi(false)
        }
        setRxMethod(method)
        syncContinueEnabledState()
      })
    })
    m.querySelectorAll('[data-pd-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        setPdMode(btn.getAttribute('data-pd-mode') || '1')
      })
    })
    document.getElementById('rxc-additional-toggle')?.addEventListener('click', () => {
      const toggle = document.getElementById('rxc-additional-toggle')
      const next = !(toggle?.classList.contains('is-active'))
      setAdditionalInfoOpen(next)
      if (next) document.getElementById('rxc_additional_data')?.focus()
    })
    document.getElementById('rxc_additional_data')?.addEventListener('input', () => {
      syncAdditionalInfoCount()
    })
    document.getElementById('rx-config-same-as-before-wrap')?.addEventListener('click', () => {
      const next = !state.sameAsBefore
      setSameAsBeforeUi(next)
      if (state.sameAsBefore) {
        state.sendLater = false
        // Keep an in-session upload if user already attached a newer file
        const prevUpload = state.uploadUrl
        const prevUploadName = state.uploadName
        const prevOcrFilled = state.ocrFilled
        fillFormFromProps(propsFromFormToFillSource())
        if (prevUpload) {
          state.uploadUrl = prevUpload
          state.uploadName = prevUploadName
          state.ocrFilled = prevOcrFilled
        }
        setRxMethod(prevUpload ? 'upload' : 'manual')
        if (prevUpload) syncUploadUi('ready')
        syncContinueEnabledState()
      }
    })
    document.getElementById('rx-config-upload-trigger')?.addEventListener('click', () => {
      document.getElementById('rx-config-rx-file')?.click()
    })
    document.getElementById('rx-config-upload-preview')?.addEventListener('click', () => {
      if (!state.uploadUrl) return
      window.open(state.uploadUrl, '_blank', 'noopener,noreferrer')
    })
    document.getElementById('rx-config-rx-file')?.addEventListener('change', async (e) => {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      setRxMethod('upload')
      await uploadRxFile(file)
    })

    bindRxDateFields()

    const rxStep = document.getElementById('rx-config-rx-step')
    if (rxStep && !rxStep.dataset.rxValidateBound) {
      rxStep.dataset.rxValidateBound = '1'
      rxStep.addEventListener('change', (e) => {
        const t = e.target
        if (!t || !t.id) return
        if (t.id === 'rx-config-rx-confirm' || t.id.indexOf('rxc_') === 0) {
          const err = document.getElementById(t.id + '-error')
          if (err) {
            err.innerHTML = ''
            err.hidden = true
          }
          t.closest('label')?.classList.remove('has-error')
          updateSoftWarningsLive()
        }
      })
    }

    document.getElementById('rx-config-summary-toggle')?.addEventListener('click', () => {
      const box = document.getElementById('rx-config-summary')
      const body = document.getElementById('rx-config-summary-body')
      const open = !box.classList.contains('is-open')
      box.classList.toggle('is-open', open)
      if (body) body.hidden = !open
      document.getElementById('rx-config-summary-toggle')?.setAttribute('aria-expanded', open ? 'true' : 'false')
    })

    document.getElementById('rx-config-try-on')?.addEventListener('click', (event) => {
      if (typeof window.openTryOn === 'function') {
        window.openTryOn(event, 'live')
      }
    })

    window.addEventListener('glasson:pd-result', (event) => {
      applyGlassonPdToRxConfig(event.detail)
    })

    ;['state:change:oph-type', 'state:change:lens-type', 'state:change:lens-color', 'state:change:lens-style', 'state:change:lens-upgrade', 'state:change:selectedVariant', 'state:change:currentProduct'].forEach((evt) => {
      document.addEventListener(evt, () => {
        if (!modal() || modal().hidden) return
        if (evt === 'state:change:oph-type') {
          const oph = window.pdp?.rx?.['oph-type']
          if (oph !== 'prescription' && window.pdp) {
            window.pdp.rxConfigLineProperties = null
          }
          rebuildAndStay()
        } else {
          updatePreviewMeta()
          syncRxConfigTryOnButton()
          updateSummary()
          syncContinueEnabledState()
          if ((evt === 'state:change:lens-type' || evt === 'state:change:selectedVariant') && currentStepId() === 'rx-entry') {
            syncAddPowerVisibility()
          }
        }
      })
    })

    m.addEventListener('click', (event) => {
      if (!event.target.closest('.dropdown-option')) return
      requestAnimationFrame(() => {
        syncContinueEnabledState()
        updateSummary()
      })
    })
  }

  function onOpen() {
    bindOnce()
    const isEdit = !!window.pdp?.editMode
    // Edit links already hydrate options from the visited variant; do not wipe them.
    // Known mega-menu / split landings keep oph-type (BL/Plain/Readers/Rx).
    if (!isEdit) {
      const landingRole = window.pdp?.ophSplitRole
      const hasKnownOphType = !!(
        window.pdp?.rx?.['oph-type'] ||
        landingRole === 'bl' ||
        landingRole === 'pl'
      )
      clearModalRequiredSelections({ preserveOphType: hasKnownOphType })
      if (typeof window.syncOphTypeAvailabilityUi === 'function') {
        window.syncOphTypeAvailabilityUi()
      }

      // Landed path OOS → show notify form in modal (same as PDP)
      const landingOos =
        typeof window.isOphLandingPathOos === 'function' && window.isOphLandingPathOos()
      if (landingOos && typeof window.showOphLandingOosInModal === 'function') {
        window.showOphLandingOosInModal()
      } else if (hasKnownOphType && !window.pdp.rx['oph-type']) {
        const typeFromRole = landingRole === 'bl' ? 'blue-light' : landingRole === 'pl' ? 'plain' : ''
        if (typeFromRole) {
          const opt = document.querySelector(
            '#oph-type-panel .dropdown-option[data-value="' + typeFromRole + '"]'
          )
          if (opt && !opt.classList.contains('is-unavailable')) opt.click()
          else if (opt && typeof window.onOphTypeUnavailableClick === 'function') {
            window.onOphTypeUnavailableClick(opt)
          }
        }
      } else {
        // Selected type became OOS → show notify for that option
        const curType = window.pdp?.rx?.['oph-type']
        if (curType) {
          const curOpt = document.querySelector(
            '#oph-type-panel .dropdown-option[data-value="' + curType + '"]'
          )
          if (curOpt?.classList.contains('is-unavailable')) {
            if (typeof window.onOphTypeUnavailableClick === 'function') {
              window.onOphTypeUnavailableClick(curOpt)
            } else {
              window.pdp.rx['oph-type'] = ''
              curOpt.classList.remove('selected')
            }
          }
        }
      }
    }
    state.index = 0
    state.sendLater = false
    setSameAsBeforeUi(false)
    state.pdMode = '1'
    state.rxMethod = ''
    state.uploadUrl = ''
    state.uploadName = ''
    state.ocrPendingConfirm = false
    state.ocrFilled = false
    state.forceShowManual = false
    state.cartRxProps = null
    state.steps = buildSteps()
    if (isEdit) {
      applySelectionsFromVisitedVariant()
      state.steps = buildSteps()
      goTo(Math.max(0, state.steps.length - 1))
    } else {
      goTo(0)
    }
    setRxMethod('')
    setPdMode('1')
    const nameEl = document.getElementById('rx-config-upload-name')
    const note = document.getElementById('rx-config-ocr-note')
    const fileInput = document.getElementById('rx-config-rx-file')
    if (nameEl) {
      nameEl.textContent = ''
      nameEl.hidden = true
    }
    if (note) {
      note.hidden = true
      note.textContent = ''
    }
    if (fileInput) fileInput.value = ''
    syncUploadUi('empty')
    syncContinueEnabledState()
    syncRxConfigTryOnButton()
    if (isEdit) updateSummary()
  }

  window.RxConfigModalSteps = {
    onOpen,
    collectRxProperties,
    updateSummary,
    updatePreviewMeta,
    syncStepVisibility,
    rebuildAndStay,
    setFinalAtcLoading
  }

  window.clearRxConfigAtcLoading = function (label) {
    setFinalAtcLoading(false, label)
  }
})()
